import * as React from 'react'
import bind from 'bind-decorator'

import videojs, { Player } from 'video.js'

require('video.js/dist/video-js.css')

import * as classnames from 'classnames'

import * as styles from './videoplayer.css'
import { observer } from 'mobx-react'
import { observable, action } from 'mobx'
import { PlayButton, BackButton } from '../elements/buttons'

import * as mime from 'mime-types'
import { StoreComponent, injectStores } from '../../../store'
import { Portal } from 'react-portal'

import { replaceFilename } from '../../../utils/StringUtils'
import { isIE } from '../../../utils/Platform'
import { SvgAndText } from '../elements/svgAndText'

const subtitleIcon = require('./subtitle.svg')

// const chatIcon = require('./chat.svg')

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'video-js': any
    }
  }
}

export interface ITextTracks {
  src: string
  kind?: string
  label?: string
  default?: boolean
  srclang?: string
}

interface IProps extends React.HTMLAttributes<HTMLVideoElement> {
  src: string
  controls: boolean
  id?: string
  overlay?: boolean
  hideOnPause?: boolean

  extraStartButtonClass?: string

  play?: boolean
  loop?: boolean
  muted?: boolean
  fluid?: boolean

  preload?: string

  textTracks?: ITextTracks[]
  textTracksDisplay?: string

  timeupdate?: (vposition: number) => void
  videoended?: () => void

  poster?: string

  backgroundImage?: string,

  playButtonText?: string
}

interface IPlayOnlyProps extends IProps {
  playStateChanged?: (value: boolean, position: number) => void
  timeupdate?: (vposition: number) => void
  videoended?: () => void

  poster?: string

}

@injectStores
@observer
export class PlayerOnly extends StoreComponent<IPlayOnlyProps> {
  videoElement: HTMLVideoElement | null
  videoElementWrapper: HTMLDivElement | null

  videoJs: Player | any

  textTracks: {[src: string]: ITextTracks} = {}

  render () {
    // console.log(this.props)
    const player = {
      __html: `
            <video-js playsInline controls="${this.props.controls}" crossorigin="anonymous" >
            </video-js>
            `
    }
    return <div className={styles.playerWrapper} dangerouslySetInnerHTML={player} ref={(elem: HTMLDivElement) => this.videoElementWrapper = elem} />
  }

  // shouldComponentUpdate () {
  //   return false
  // }

  componentDidMount () {
    if (this.videoElementWrapper && this.videoElementWrapper.firstChild) {
      this.videoElement = this.videoElementWrapper.firstElementChild as HTMLVideoElement
      let src = this.props.src
      if (isIE) {
        src = replaceFilename(src, 'ie-video.mp4')
      }

      this.videoJs = videojs(this.videoElement, {
        fluid: this.props.fluid,
        autoplay: this.props.play,
        sources: [{
          src: src,
          type: mime.lookup(src) || 'video/mp4'
        }],
        muted: this.props.muted,
        loop: this.props.loop,
        controls: this.props.controls,
        poster: this.props.poster,
        errorDisplay: false,
        preload: 'auto',
        html5: {
          nativeTextTracks: false
        }
      } as any)
      // hack
      // const track = this.videoJs.addRemoteTextTrack({ src: 'https://storage.googleapis.com/docmine-meili-public/playground/subtitles.vtt' }, true)
      // console.log(track)
      // register important events
      this.videoJs.on('pause', () => this.video_on_pause())
      this.videoJs.on('play', () => this.video_on_play())
      this.videoJs.on('timeupdate', () => this.video_on_progress())
      this.videoJs.on('ended', () => this.video_on_ended())

      // this.videoJs.ready(() => {
      //   // this.updateTextTracks()
      // })
      this.componentDidUpdate(this.props)
    }
  }

  componentDidUpdate (prevProps: IPlayOnlyProps) {
    if (this.videoElement) { // keep track of the props we support
      // TODO speed up by compunting changes!
      // class
      if (prevProps.className) {
        for (const c of prevProps.className.split(' ')) {
          this.videoElement.classList.remove(c)
        }
      }
      if (this.props.className) {
        for (const c of this.props.className.split(' ')) {
          this.videoElement.classList.add(c)
        }
      }
      // add required classes
      this.videoElement.classList.add('video-js', 'vjs-default-skin', 'vjs-big-play-centered')

      // Android hack
      if (this.videoElement.firstChild) {
        const v = this.videoElement.firstChild as any
        v.setAttribute('poster', this.props.poster || './transparent.png')
      }
    }

    if (this.props.play) {
      if (this.videoJs.paused()) {
        this.videoJs.play()
      }
    } else {
      this.videoJs.pause()
      // this.videoJs.exitFullscreen()
    }

    if (this.props.muted) {
      this.videoJs.muted(true)
    } else {
      this.videoJs.muted(false)
    }

    if (this.props.loop) {
      this.videoJs.loop(true)
    } else {
      this.videoJs.loop(false)
    }

    if (this.props.controls) {
      this.videoJs.controls(true)
    } else {
      this.videoJs.controls(false)
    }
    // this.updateTextTracks()

  }

  componentWillUnmount () {
    this.videoJs.dispose()
    this.videoJs = null
    if (this.videoElement) {
      while (this.videoElement.firstChild) {
        this.videoElement.removeChild(this.videoElement.firstChild)
      }
    }
    this.videoElement = null
  }

  video_on_pause (): void {
    // seeking triggers pause
    if (this.videoJs.seeking()) {
      return
    }
    if (this.props.playStateChanged) {
      this.props.playStateChanged(false, this.videoJs.currentTime())
    }

    // this.videoJs.exitFullscreen()
  }

  video_on_play (): void {

    let settings = this.videoJs.textTrackSettings
    settings.setValues({
      'backgroundColor': '#000',
      'backgroundOpacity': '0',
      'edgeStyle': 'uniform',
      'color': '#FFF',
      'textOpacity': '1.0',
      'fontPercent': 0.75
    })
    settings.updateDisplay()

    this.updateTextTracks()
    if (this.props.playStateChanged) {
      this.props.playStateChanged(true, this.videoJs.currentTime())
    }
  }

  video_on_progress (): void {
    if (this.props.timeupdate) {
      this.props.timeupdate(this.videoJs.currentTime())
    }
  }

  video_on_ended (): void {
    if (this.props.videoended) {
      this.props.videoended()
    }
  }

  updateTextTracks () {
    if (! this.videoJs) return

    if (this.props.textTracks) {
      for (const track of this.props.textTracks) {
        if (this.textTracks[track.src]) continue

        const t = this.videoJs.addRemoteTextTrack(track, true)
        if (t) {
          // console.log(t)
          this.textTracks[track.src] = track
        }
      }

      const tracks = this.videoJs.remoteTextTracks()
      for (const track of tracks) {
        if (track.language === this.props.textTracksDisplay) {
          track.mode = 'showing'
        } else {
          track.mode = track.kind === 'metadata' ? 'hidden' : 'disabled'
        }
      }
    }

  }
}

let uniqueId = 0

@injectStores
@observer
export class VideoPlayer extends StoreComponent<IProps> {
  @observable play = false
  @observable opened = false

  @observable id: string
  firstTime: boolean

  constructor (props: any, context: any) {
    super(props, context)

    this.play = this.props.play || false
    this.id = this.props.id || this.props.src + `${uniqueId++}`
    this.firstTime = true
  }

  render () {
    const sbutton = classnames(
      styles.button,
      {
        [styles.hidden]: this.opened || !this.props.controls || this.play,
        [this.props.extraStartButtonClass + '']: !!this.props.extraStartButtonClass
      }
    )

    // todo make this.props.play change work!
    const play = this.play && (this.id === this.store.storeCommon.currentStory.id || this.firstTime)
    this.firstTime = false

    let isVisible = false
    let playerOverlay: JSX.Element | null = null
    if (this.props.overlay) {
      if (this.opened) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = null

      }
      const soverlay = classnames(styles.overlay, {
        [styles.hidden]: !this.opened
      }
      )
      if (this.opened) {
        isVisible = true
        playerOverlay = (
        <Portal >
          <div className={soverlay} onClick={() => this.close()} >
            <div className={styles.overlayTitle} onClick={() => this.close()} >
              <BackButton onClick={() => this.close()} />
              <SvgAndText img={subtitleIcon} className={styles.overlaySubtitle}> Untertitle </SvgAndText>
            </div>
            <div className={(this.props.className || '') + ' ' + styles.overlayPlayerPlaceholder} >
            <div className={styles.overlayPlayerWrapper} onClick={ event => {
              event.stopPropagation()
            } } >
              <PlayerOnly fluid={true} poster={this.getBackground()} {...this.props} className={styles.player} src={this.props.src} play={play} playStateChanged={this.playStateChanged} timeupdate={this.timeUpdate}
              videoended={() => {
                this.opened = false
                this.props.videoended && this.props.videoended()
              }} />
            </div>
            </div>
          </div>
        </Portal>
        )
      }
    } else {
      isVisible = (!this.props.controls || (this.play && !this.props.hideOnPause))
      document.body.style.overflow = 'default'
      const splayer = classnames({
        [styles.hidden]: !isVisible
      })
      playerOverlay = <PlayerOnly fluid={true} poster={this.getBackground()} {...this.props} className={splayer} src={this.props.src} play={play} playStateChanged={this.playStateChanged} timeupdate={this.timeUpdate} videoended={this.props.videoended} />
    }

    return (
      <div className={(this.props.className || '') + ' ' + styles.playerContainer} onClick={(this.props.onClick as any)}
        style={{
          backgroundImage: isVisible ? `url('${this.getBackground()}')` : undefined,
          backgroundSize: 'cover'
        }}
      >
        {playerOverlay}
        <div className={styles.controls}>
          <PlayButton className={sbutton} onClick={() => this.onStart()} > {this.props.playButtonText} </PlayButton>
        </div>
      </div>
    )
  }

  @action
  onStart () {
    this.play = true
    if (this.props.overlay) {
      this.opened = true
    }

    this.store.storeCommon.currentStory.id = this.id
  }

  @action
  close () {
    this.opened = false
    this.play = false
  }

  @bind
  @action
  playStateChanged (value: boolean, position: number) {
    this.play = value
    if (value) {
      this.store.storeCommon.currentStory.id = this.id
      this.store.storeCommon.currentStory.position = position
    } else {
      if (this.store.storeCommon.currentStory.id === this.id) {
        this.store.storeCommon.currentStory.position = position
        this.store.storeCommon.currentStory.id = null
      }
    }

  }

  @bind
  @action
  timeUpdate (position: number) {
    if (this.store.storeCommon.currentStory.id === this.id) {
      this.store.storeCommon.currentStory.position = position
    }
  }

  componentWillUnmount () {
    if (this.store.storeCommon.currentStory.id === this.id) {
      this.store.storeCommon.currentStory.id = null
    }
  }

  getBackground () {
    if (this.props.backgroundImage) {
      return this.props.backgroundImage
    }

    return replaceFilename(this.props.src, 'firstframe.jpg')
  }

}



// WEBPACK FOOTER //
// ./src/modules/common/video/videoplayer.tsx